import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Moves keyboard/assistive-technology focus into the newly routed view after each
 * client-side navigation. Without this, SPA navigation leaves focus on the control
 * that triggered it (or resets it to <body>), forcing keyboard and screen-reader
 * users to tab from the top of the page again (WCAG 2.4.3 Focus Order).
 *
 * Focus is moved to the routed <main> landmark, or the page <h1> as a fallback —
 * where the meaningful content of the new view begins. The initial page load and
 * query/fragment-only changes are skipped, so we don't fight deep-link focus or
 * steal focus during in-page filtering, tab params, or pagination.
 */
@Injectable({ providedIn: 'root' })
export class RouteFocusService {
  private readonly _router = inject(Router);
  private readonly _document = inject(DOCUMENT);
  private readonly _zone = inject(NgZone);
  private readonly _destroyRef = inject(DestroyRef);

  private _lastPath: string | null = null;
  private _initialized = false;

  public initialize(): void {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    this._router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((event) => this._onNavigationEnd(event));
  }

  private _onNavigationEnd(event: NavigationEnd): void {
    const path = event.urlAfterRedirects.split('#')[0].split('?')[0];

    if (this._lastPath === null || this._lastPath === path) {
      this._lastPath = path;
      return;
    }
    this._lastPath = path;

    this._zone.runOutsideAngular(() => {
      setTimeout(() => this._focusMainContent(), 0);
    });
  }

  private _focusMainContent(): void {
    const target =
      this._document.querySelector<HTMLElement>('main') ??
      this._document.querySelector<HTMLElement>('h1');
    if (!target) {
      return;
    }

    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    }
    target.focus({ preventScroll: false });
  }
}
